import { useState, useEffect, type Dispatch, type SetStateAction } from "react";
import { showAlert } from '@/components/AppDialog';
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { ACCENT, SHELL_BG } from "../components/AppShell";
import MobileNav from "../components/MobileNav";
import UserProfileMenu from "../components/UserProfileMenu";
import { API_BASE_URL } from '@/utils/env';

const YEARS = [2026, 2025, 2024, 2023];

interface FinData {
  revenue: string;
  totalExpenses: string;
  cash: string;
  totalEquity: string;
}

const EMPTY: FinData = { revenue: '', totalExpenses: '', cash: '', totalEquity: '' };

function n(s: string) { return parseFloat(s) || 0; }

/* Figures are entered and shown in dirhams — no second currency, no conversion. */
function fmt(v: number) {
  return `AED ${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

const inputCls = "w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm acc-ring focus:outline-none";

// Defined at module scope (NOT inside the page component) so its identity stays
// stable across renders — otherwise React remounts the <input> on every keystroke
// and focus is lost.
function CurrencyInput({ label, field, note, data, setData }: {
  label: string;
  field: keyof FinData;
  note?: string;
  data: FinData;
  setData: Dispatch<SetStateAction<FinData>>;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#374151] mb-1.5">{label}</label>
      {note && <p className="text-xs text-gray-400 mb-1">{note}</p>}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 select-none">
          AED
        </span>
        {/* Padded clear of the prefix: "AED" is ~30px at text-sm and starts 12px
            in, so anything less than this ran the figure into the D. */}
        <input type="number" min="0" value={data[field]} onChange={e => setData(p => ({ ...p, [field]: e.target.value }))} placeholder="0"
          className={`${inputCls} pl-[52px]`} />
      </div>
    </div>
  );
}

export default function FinancialsPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(YEARS[0]);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState<FinData>(EMPTY);
  // Full { [year]: FinData } map — kept so a save writes a proper nested object
  // (backend RTDB update() rejects dotted keys like "financials.2026").
  const [allFin, setAllFin] = useState<Record<string, any>>({});

  useEffect(() => {
    (async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) { navigate('/login'); return; }
        const user = JSON.parse(userStr);
        const tokensStr = localStorage.getItem('authTokens');
        const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
        const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
          headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) }
        });
        if (resp.ok) {
          const json = await resp.json();
          const fmap = (json.user?.financials && typeof json.user.financials === 'object') ? json.user.financials : {};
          setAllFin(fmap);
          setData(fmap[period] ? { ...EMPTY, ...fmap[period] } : EMPTY);
        }
      } catch (e) { console.error(e); }
      finally { setFetching(false); }
    })();
  }, [navigate, period]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) { navigate('/login'); return; }
      const user = JSON.parse(userStr);
      const tokensStr = localStorage.getItem('authTokens');
      const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
      const nextFin = { ...allFin, [period]: { ...data, updatedAt: new Date().toISOString() } };
      const resp = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
        body: JSON.stringify({ financials: nextFin })
      });
      if (!resp.ok) throw new Error('Failed to save');
      setAllFin(nextFin);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error(e);
      showAlert('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => { localStorage.removeItem('user'); navigate('/'); };

  const netProfit = n(data.revenue) - n(data.totalExpenses);
  const isLoss = netProfit < 0;

  if (fetching) return (
    <div className="min-h-screen" style={{ background: SHELL_BG }}>
      <Sidebar onLogout={handleSignOut} />
      <main className="md:ms-64 flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: ACCENT, borderTopColor: "transparent" }} />
      </main>
    </div>
  );

  return (
    <div className="min-h-screen text-slate-900" style={{ background: SHELL_BG }}>
      <Sidebar onLogout={handleSignOut} />
      <main className="md:ms-64 flex-1 overflow-y-auto relative min-h-screen" style={{ background: SHELL_BG }}>
        <MobileNav onLogout={handleSignOut} />
        <div className="absolute top-6 end-8 z-10"><UserProfileMenu /></div>

        <div className="mx-auto max-w-[720px] px-6 py-12 pb-28">
          <div className="mb-2 flex items-center gap-1 text-sm text-[#6B7280]">
            <button onClick={() => navigate('/dashboard')} className="hover:text-gray-900">My Applications</button>
            <span>/</span>
            <span className="text-[#111827] font-medium">Financials</span>
          </div>

          <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-bold text-[#111827] mb-1">Financials</h1>
              <p className="text-sm text-[#6B7280]">Enter values in dirhams.</p>
            </div>
            <div className="flex items-center gap-2">
              <select value={period} onChange={e => setPeriod(Number(e.target.value))}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white acc-ring focus:outline-none">
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {/* Info bar */}
          <div className="acc-soft-bg acc-line acc-ink px-4 py-3 border rounded-xl mb-4 text-sm">
            Annual financial snapshot. MRR/ARR are tracked separately in KPIs.
          </div>

          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="text-sm font-semibold text-[#111827]">Your financial information</div>
              <div className="text-xs text-[#6B7280]">Annual figures for {period}</div>
            </div>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CurrencyInput label="Revenue" field="revenue" note="Annual total revenue" data={data} setData={setData} />
                <CurrencyInput label="Total expenses" field="totalExpenses" note="All costs for the year" data={data} setData={setData} />

                {/* Net profit — auto-derived */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-[#374151] mb-1.5">Net profit / loss</label>
                  <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border ${isLoss ? 'bg-[#FBE3E0] border-[#e8b4b0] text-[#B23A2E]' : 'bg-[#E2F4EA] border-[#b2dfc6] text-[#15824E]'}`}>
                    <span className="text-sm font-semibold">
                      {n(data.revenue) || n(data.totalExpenses) ? fmt(netProfit) : '-'}
                    </span>
                    <span className="text-xs">{isLoss ? 'loss' : 'profit'}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Revenue − total expenses</p>
                </div>

                <CurrencyInput label="Cash balance" field="cash" note="Current cash, shared with KPIs" data={data} setData={setData} />
                <CurrencyInput label="Total equity" field="totalEquity" note="Shareholders' equity" data={data} setData={setData} />
              </div>
            </div>
          </div>
        </div>

        {/* Fixed save bar */}
        <div className="fixed bottom-0 md:left-64 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3 flex items-center justify-end gap-3 z-20">
          {saved && (
            <span className="text-sm text-green-600 font-medium flex items-center gap-1">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Saved
            </span>
          )}
          <button onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-[#374151] border border-gray-300 rounded-lg hover:bg-gray-50 transition">
            Cancel
          </button>
          <button onClick={handleSave} disabled={loading}
            className="btn-acc px-5 py-2 text-sm font-medium rounded-lg transition">
            {loading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </main>
    </div>
  );
}
